// @ts-nocheck
// Registrations list: searchable, filterable card grid. Creation happens via
// CreateRegistrationDialog (the step wizard is retired); editing via the
// sectioned edit page; destructive delete requires typing DELETE.
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Link, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus, Search, MoreHorizontal, Pencil, Copy, Trash2, CheckCircle2, CircleOff,
  CalendarClock, Layers, ClipboardList,
} from 'lucide-react';
import useRegistrationStore from '@store/registrationStore';
import useOrganizationStore from '@store/organizationStore';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { CardGridSkeleton } from '@/components/Page/PageSkeletons';
import CloneRegistrationModal from '@/components/CloneRegistrationModal';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import CreateRegistrationDialog from '@/components/CreateRegistrationDialog';
import getRegistrationName from '@staty/shared/utils/getRegistrationName';

const closeDateLabel = (registration) => {
  if (!_.get(registration, 'config.gateByEndDate') || !registration.endDate) return null;
  const date = new Date(registration.endDate);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    ...(registration.timeZone ? { timeZone: registration.timeZone } : {}),
  });
};

function RegistrationListAdmin() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { leagueName = '' } = useOutletContext() as any;
  const { organizations } = useOrganizationStore();
  const { getToken } = useAuth();
  const organization = _.find(organizations, { name: leagueName.toLowerCase() });

  const { registrations, getAllRegistrations, deleteRegistration } = useRegistrationStore((state: any) => ({
    registrations: state.registrations,
    getAllRegistrations: state.getAllRegistrations,
    deleteRegistration: state.deleteRegistration(getToken),
  }));

  const [search, setSearch] = useState('');
  const [statusTab, setStatusTab] = useState('all');
  const [removingRegistration, setRemovingRegistration] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [cloningRegistration, setCloningRegistration] = useState(null);
  // ?create=tournament|league&parentId=X deep-links straight into the dialog
  // (Competitions tab, legacy wizard-URL redirects, and the ⌘K palette's
  // "Create registration" action). Effect-based rather than a mount-time
  // useState read so the palette action also works while ALREADY on this page;
  // the param is cleared inside the guard (replace:true) to avoid loops.
  const [createOpen, setCreateOpen] = useState(false);
  const [createType, setCreateType] = useState<'tournament' | 'league'>('tournament');
  const [createParentId, setCreateParentId] = useState('');
  useEffect(() => {
    const c = searchParams.get('create');
    if (c) {
      setCreateType(c === 'league' ? 'league' : 'tournament');
      setCreateParentId(searchParams.get('parentId') || '');
      setCreateOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Skeleton only until the FIRST fetch resolves — and only when the store has
  // nothing cached; with cached data we paint it immediately and refresh
  // silently. Never show the empty state while loading (it used to flash
  // "No registrations yet" at orgs that had them).
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    Promise.resolve(getAllRegistrations(leagueName)).finally(() => setLoaded(true));
  }, []);

  const counts = useMemo(() => ({
    all: registrations.length,
    active: registrations.filter((r) => r.active).length,
    inactive: registrations.filter((r) => !r.active).length,
  }), [registrations]);

  const visible = useMemo(() => {
    let list = [...registrations];
    if (statusTab === 'active') list = list.filter((r) => r.active);
    if (statusTab === 'inactive') list = list.filter((r) => !r.active);
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => getRegistrationName(r).toLowerCase().includes(q));
    // Newest first.
    return _.orderBy(list, [(r) => new Date(r.created || 0).getTime()], ['desc']);
  }, [registrations, statusTab, search]);

  const onDelete = async () => {
    if (!removingRegistration) return;
    setDeleting(true);
    try {
      await deleteRegistration(removingRegistration._id);
      setRemovingRegistration(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Registrations"
        description="Sign-up forms for your tournaments and seasons."
        actions={(
          <Button onClick={() => setCreateOpen(true)} data-tour="create-registration-btn">
            <Plus className="mr-2 h-4 w-4" /> Create Registration
          </Button>
        )}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={statusTab} onValueChange={setStatusTab}>
          <TabsList>
            <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="active">Active ({counts.active})</TabsTrigger>
            <TabsTrigger value="inactive">Inactive ({counts.inactive})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search registrations…"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {!loaded && registrations.length === 0 ? (
        <CardGridSkeleton />
      ) : registrations.length === 0 ? (
        <div className="rounded-xl border border-dashed p-12 text-center">
          <ClipboardList className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <h2 className="mt-3 text-lg font-semibold">No registrations yet</h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
            Create a registration to start accepting team sign-ups for a tournament or season.
          </p>
          <Button className="mt-4" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Create Registration
          </Button>
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">
          No registrations match{search ? ` “${search}”` : ' this filter'}.
        </div>
      ) : (
        <div className="stagger-children grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-tour="registration-list">
          {visible.map((registration) => {
            const name = getRegistrationName(registration);
            const closes = closeDateLabel(registration);
            const contests = _.get(registration, 'contests.length');
            return (
              <div
                key={registration._id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/admin/registration/${registration._id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/admin/registration/${registration._id}`); }}
                className={`group relative flex cursor-pointer flex-col rounded-xl border bg-card p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  registration.active ? 'border-border' : 'border-border bg-muted/30'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    {/* Wrap long tournament/season names instead of truncating. */}
                    <div className="break-words text-base font-semibold text-foreground group-hover:text-primary">
                      {name}
                    </div>
                    <div className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">
                      {registration.contestType === 'tournament' ? 'Tournament' : 'Season'}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      onClick={(e) => e.stopPropagation()}
                      className="rounded p-1 text-muted-foreground opacity-70 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
                      aria-label="Registration actions"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => navigate(`/admin/registration/${registration._id}/edit`)}>
                        <Pencil className="mr-2 h-4 w-4" /> Edit Registration
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setCloningRegistration(registration)}>
                        <Copy className="mr-2 h-4 w-4" /> Clone Registration
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setRemovingRegistration(registration)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete Registration
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {registration.active ? (
                    <Badge className="gap-1 bg-success/10 text-success hover:bg-success/10">
                      <CheckCircle2 className="h-3 w-3" /> Active
                    </Badge>
                  ) : (
                    <Badge variant="secondary" className="gap-1 text-muted-foreground">
                      <CircleOff className="h-3 w-3" /> Inactive
                    </Badge>
                  )}
                  {closes && (
                    <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
                      <CalendarClock className="h-3 w-3" /> Closes {closes}
                    </Badge>
                  )}
                  {_.isNumber(contests) && (
                    <Badge variant="outline" className="gap-1 font-normal text-muted-foreground">
                      <Layers className="h-3 w-3" /> {contests} contest{contests === 1 ? '' : 's'}
                    </Badge>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDeleteDialog
        open={!!removingRegistration}
        onOpenChange={(o) => { if (!o) setRemovingRegistration(null); }}
        title="Delete Registration"
        description={(
          <span>
            This permanently removes <strong>{removingRegistration ? getRegistrationName(removingRegistration) : ''}</strong>{' '}
            from your admin. Its public sign-up page stops working. Teams that already registered are
            not deleted, but they will no longer be reachable from a registration dashboard.
          </span>
        )}
        onConfirm={onDelete}
        loading={deleting}
      />

      <CloneRegistrationModal
        isOpen={!!cloningRegistration}
        onClose={() => setCloningRegistration(null)}
        registrationToClone={cloningRegistration}
      />

      <CreateRegistrationDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        organization={organization}
        leagueName={leagueName}
        initialType={createType === 'league' ? 'league' : 'tournament'}
        initialParentId={createParentId}
      />
    </PageContainer>
  );
}

export default RegistrationListAdmin;
